import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Clock,
  Calendar,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import MainLayout from "@/components/layouts/MainLayout";

const Contact = () => {
  // Form submission handler (placeholder)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would send the form data to a server
    alert("Thank you for your message. We'll get back to you soon!");
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Numberz Insight</title>
        <meta
          name="description"
          content="Contact Numberz Insight for professional tax and accounting services. Get in touch with our team of experts in Melbourne and Launceston."
        />
        <meta
          name="keywords"
          content="contact, tax services, accounting firm, business registration, financial planning, Australian taxation"
        />
      </Helmet>

      <MainLayout>
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative bg-gradient-to-b from-primary/10 to-background dark:from-primary/5 dark:to-background py-16 md:py-24">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-left"
                >
                  <Badge
                    variant="outline"
                    className="mb-4 px-3 py-1 border-primary/30 text-primary"
                  >
                    <Clock className="w-3 h-3 mr-1 inline" /> Available 9am-5pm
                    AEST
                  </Badge>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                    Get in Touch
                  </h1>
                  <p className="text-xl text-muted-foreground mb-6">
                    Have questions about our services? Our team of experts is
                    here to help you.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 mr-3 text-primary" />
                      <span>1300 123 435</span>
                    </div>
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 mr-3 text-primary" />
                      <a
                        href="mailto:launceston@numberz.au"
                        className="text-primary hover:underline"
                      >
                        launceston@numberz.au
                      </a>
                    </div>
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="rounded-xl overflow-hidden shadow-xl"
                >
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80"
                    alt="Professional office environment"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Contact Form Section */}
          <section className="py-16 bg-muted/30 dark:bg-muted/10">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Send Us a Message</CardTitle>
                      <CardDescription>
                        Fill out the form below and we'll get back to you as
                        soon as possible.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input
                              id="firstName"
                              placeholder="Enter your first name"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input
                              id="lastName"
                              placeholder="Enter your last name"
                              required
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="Enter your email"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                              id="phone"
                              placeholder="Enter your phone number"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>I'm interested in:</Label>
                          <RadioGroup defaultValue="tax-return">
                            <div className="flex flex-wrap gap-4">
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="tax-return"
                                  id="tax-return"
                                />
                                <Label htmlFor="tax-return">Tax Return</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="business-registration"
                                  id="business-registration"
                                />
                                <Label htmlFor="business-registration">
                                  Business Registration
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="financial-planning"
                                  id="financial-planning"
                                />
                                <Label htmlFor="financial-planning">
                                  Financial Planning
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="other" id="other" />
                                <Label htmlFor="other">Other</Label>
                              </div>
                            </div>
                          </RadioGroup>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Message</Label>
                          <Textarea
                            id="message"
                            placeholder="How can we help you?"
                            rows={5}
                            required
                          />
                        </div>

                        <Button type="submit" className="w-full">
                          <Send className="mr-2 h-4 w-4" /> Send Message
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <Card>
                    <CardHeader>
                      <CardTitle>Our Offices</CardTitle>
                      <CardDescription>
                        Visit us at one of our locations
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-2 flex items-center">
                          <MapPin className="h-5 w-5 mr-2 text-primary" />
                          Melbourne Office
                        </h3>
                        <p className="text-muted-foreground">
                          Unit 41/2 Fastline Rd
                          <br />
                          Truganina VIC 3029
                          <br />
                          1300 123 435
                          <br />
                          <a
                            href="mailto:tax.agents@numberz.au"
                            className="text-primary hover:underline"
                          >
                            tax.agents@numberz.au
                          </a>
                        </p>
                        <div className="mt-2">
                          <Badge
                            variant="outline"
                            className="flex items-center w-fit"
                          >
                            <Clock className="w-3 h-3 mr-1" /> 9:00 AM - 5:00 PM
                            (AEST)
                          </Badge>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-semibold mb-2 flex items-center">
                          <MapPin className="h-5 w-5 mr-2 text-primary" />
                          Launceston Office
                        </h3>
                        <p className="text-muted-foreground">
                          Office 2, 187 Brisbane St
                          <br />
                          Launceston TAS 7250
                          <br />
                          1300 123 435
                          <br />
                          <a
                            href="mailto:launceston@numberz.au"
                            className="text-primary hover:underline"
                          >
                            launceston@numberz.au
                          </a>
                        </p>
                        <div className="mt-2">
                          <Badge
                            variant="outline"
                            className="flex items-center w-fit"
                          >
                            <Clock className="w-3 h-3 mr-1" /> 9:00 AM - 5:00 PM
                            (AEST)
                          </Badge>
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <h3 className="text-lg font-semibold mb-2">
                          Book an Appointment
                        </h3>
                        <Button variant="outline" className="w-full" asChild>
                          <Link to="/dashboard/support">
                            <Calendar className="mr-2 h-4 w-4" /> Schedule a
                            Meeting
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle>Quick Response</CardTitle>
                      <CardDescription>
                        We aim to respond to all inquiries promptly
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 mr-2 text-primary mt-0.5" />
                          <p className="text-sm text-muted-foreground">
                            Email responses within 24 hours
                          </p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 mr-2 text-primary mt-0.5" />
                          <p className="text-sm text-muted-foreground">
                            Phone calls returned same business day
                          </p>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 mr-2 text-primary mt-0.5" />
                          <p className="text-sm text-muted-foreground">
                            Urgent matters prioritized
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Map Section */}
          <section className="py-16">
            <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  Find Our Melbourne Office
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Unit 41/2 Fastline Rd, Truganina VIC 3029
                </p>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg h-[400px] border border-border">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3151.835434509639!2d144.79992871531957!3d-37.82331077975028!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad689d320a9b0e9%3A0x4b7d796cb4ef1d83!2sUnit%2041%2F2%20Fastline%20Rd%2C%20Truganina%20VIC%203029%2C%20Australia!5e0!3m2!1sen!2sus!4v1713663553000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Melbourne Office Location Map - Unit 41/2 Fastline Rd, Truganina"
                ></iframe>
              </div>

              <div className="flex justify-center mt-4">
                <Button asChild>
                  <a
                    href="https://maps.google.com/?q=Unit+41/2+Fastline+Rd,+Truganina+VIC+3029,+Australia"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin className="mr-2 h-4 w-4" /> Get Directions
                  </a>
                </Button>
              </div>
            </div>
          </section>
        </main>
      </MainLayout>
    </>
  );
};

export default Contact;
